<template>
  <div>
    <WFormCheckbox
      :modelValue="modelValue"
      :checked="checked"
      @change="handleChange"
      :name="nameUnique"
      :checkboxSize="checkboxSize"
      :errorMessage="errorMessage"
      :trueValue="trueValue"
      :falseValue="falseValue"
      :borderColor="borderColor"
      :borderWidth="borderWidth"
      :type="type"
      :role="role"
      :disabled="disabled"
      v-orphans
    >
      <template #label>
        <slot name="label">
          <span v-if="rules?.includes('required')" class="text-danger">*</span>
          <div v-if="label" v-html="label" class="d-inline" />
        </slot>
      </template>
      <template #append>
        <slot name="append" />
      </template>
    </WFormCheckbox>
  </div>
</template>

<script lang="ts" setup>
import type { Maybe, FormCheckboxType } from '@/types'
import { useField } from 'vee-validate'
import { computed } from 'vue'
import { toRef } from 'vue'
import type { Ref } from 'vue'

type Props = {
  name: string
  checkboxSize?: number
  label?: string
  trueValue?: Maybe<boolean | string>
  falseValue?: Maybe<boolean | string>
  borderColor?: string
  borderWidth?: number
  rules?: string
  type?: FormCheckboxType
  role?: 'checkbox' | 'switch' | 'radio'
  modelValue?: Maybe<string | boolean | Array<string | number>>
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  checkboxSize: 24,
  trueValue: true,
  falseValue: null,
  borderColor: '#266dd3',
  borderWidth: 2,
  type: 'checkbox',
  role: 'checkbox',
  disabled: false
})

const nameRef: Readonly<Ref<string>> = toRef(props, 'name')
const rulesRef: Readonly<Ref<Maybe<string>>> = toRef(props, 'rules')

// In case of checkobox group, the nameRef need to be the same for all checkboxes in vee-validate
// we need unique name for each WFormCheckbox,
// so that clicking its label will toggle the correct checkbox
const nameUnique = computed<string>(() => `${props.name}_${props.trueValue}`)

const { checked, errorMessage, handleChange } = useField(nameRef, rulesRef, {
  type: props.type,
  checkedValue: props.trueValue,
  uncheckedValue: props.falseValue,
  syncVModel: true
})
</script>
